/**
 * Routing.
 *
 * `RequireAuth` and `RequireRole` shape what the browser shows. They are not
 * the security boundary: every API call is authenticated and authorized
 * server-side, so bypassing these in the client gains nothing but an empty
 * screen and a 401.
 */
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { AuthProvider, useAuth } from './lib/auth';
import { AppLayout } from './components/AppLayout';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { NewProposalPage } from './pages/NewProposalPage';
import { ProposalPage } from './pages/ProposalPage';
import { TemplatesPage } from './pages/TemplatesPage';
import { AdministrationPage } from './pages/AdministrationPage';
import type { Role } from './lib/api';

function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingScreen />;
  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return <>{children}</>;
}

function RequireRole({ roles, children }: { roles: Role[]; children: ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user || !roles.includes(user.role)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function LoadingScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <p className="text-[13px] text-muted">Loading…</p>
    </div>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            element={
              <RequireAuth>
                <AppLayout />
              </RequireAuth>
            }
          >
            <Route index element={<DashboardPage />} />
            <Route path="/proposals/new" element={<NewProposalPage />} />
            <Route path="/proposals/:proposalId" element={<ProposalPage />} />
            <Route path="/templates" element={<TemplatesPage />} />
            <Route
              path="/administration"
              element={
                <RequireRole roles={['ADMIN']}>
                  <AdministrationPage />
                </RequireRole>
              }
            />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
